import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { when } from 'lit/directives/when.js';
import { LangFranchiseeProjectResourceView } from '../../../../localization/scripts/dealer-franchisee/projects/views/franchisee-project-resource-view-lang';
import { ItemReference } from '../../../../webmodule-common/cache/definitions/cache-item-reference.js';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef,
  WebmoduleSelectEvent
} from '../../../../webmodule-common/components/src/webmodule-components.js';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid.js';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter.js';
import { fireQuickErrorToast } from '../../../../webmodule-common/other/toast-away.js';
import { refreshButton } from '../../../../webmodule-common/other/ui/command-action.js';
import { EventTemplate, Snippet } from '../../../../webmodule-common/other/ui/events.js';
import { LitTableWrapper } from '../../../../webmodule-common/other/ui/littable-view.js';
import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation.js';
import { RequestPage, ResultPaginated } from '../../../../webmodule-common/other/ui/RequestPage.js';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver.js';
import { containsFlag } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { PurchaseOrder } from '../../../api/dealer-api-interface-franchisee.js';
import { ProjectState, Resource, ResourceType } from '../../../api/dealer-api-interface-project.js';
import { QuotePriceCalculation, QuoteState } from '../../../api/dealer-api-interface-quote.js';
import { ProjectContainerManager } from '../../../projects/data/project-container.js';
import {
  EventResource,
  ProjectResourceView,
  ProjectResourceViewOptions
} from '../../../projects/views/project-resource-view.js';
import {
  getPurchaseOrderNumberFormatted,
  getPurchaseOrderStatus
} from '../../../purchase-orders/data/purchase-order-helper-functions.js';
import { resourcePurchaseOrder } from '../../../purchase-orders/ui/launcher.js';
import { getQuoteNumberFormatted, getQuoteStatus } from '../../../quotes/data/quote-helper-functions.js';
import { launchQuote, resourceQuote } from '../../../quotes/ui/launcher.js';
import { cache } from '../../cache-impl/cache-registry.js';
import { ProjectResourceLink } from '../../cache-impl/resource-link.js';
import { monitorQuoteCopyCompletion } from '../../quotes/data/quote-copy-completion-monitor.js';
import { QuoteSetSiblingManager } from '../../quotes/data/quote-state-engine.js';
import { getQuoteContainer, getQuoteContainerManager } from '../../quotes/quote-ui-adapter.js';

interface FranchiseeProjectResourceTableOptions {
  projectManager: ProjectContainerManager;
}

interface FranchiseProjectResourceQuoteTableOptions extends FranchiseeProjectResourceTableOptions {
  copyResourceEvent: EventResource;
}

@customElement('wm-franchiseeprojectresourcequotetable')
export class FranchiseeProjectResourceQuoteTable extends LitTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  quoteCache = cache().quote;
  userCache = cache().userProfile;
  copyResourceEvent: EventResource;

  constructor(options: FranchiseProjectResourceQuoteTableOptions) {
    super();
    this.projectManager = options.projectManager;
    this.copyResourceEvent = options.copyResourceEvent;
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.Quote);
    if (!items)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    const quoteKeys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

    //if there are any other callbacks we need to do, add them to the
    //all array which will let them run at the same time
    await Promise.all([this.quoteCache.preFetch(quoteKeys)]);

    const authorKeys =
      items
        .map(x => {
          const summary = this.getQuoteSummary(x.resourceId);
          return summary ? summary.assignedToUserId : null;
        })
        .filter(key => key !== null) ?? [];

    await this.userCache.preFetch(authorKeys);

    items.sort((a1: Resource, a2: Resource) => {
      const s1 = this.getQuoteSummary(a1.resourceId);
      const s2 = this.getQuoteSummary(a2.resourceId);
      const t1 = getQuoteNumberFormatted(s1);
      const t2 = getQuoteNumberFormatted(s2);
      return t1.localeCompare(t2);
    });
    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleLitTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.date(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-120 project-quote-created-date',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return '';

          const dt = new Date(summary.dateCreated);
          return `${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteNo(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-180 project-quote-number',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);
          const copying = summary ? containsFlag(summary?.state, QuoteState.Copying) : false;
          return this.quoteLink(resource, getQuoteNumberFormatted(this.getQuoteSummary(resource.resourceId)), copying);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteTitle(),
        fieldName: 'resourceId',
        classes: 'colpx-50 project-quote-title',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);
          const copying = summary ? containsFlag(summary?.state, QuoteState.Copying) : false;
          return this.quoteLink(resource, this.getQuoteSummary(resource.resourceId)?.title ?? '', copying);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.author(),
        fieldName: 'resourceId',
        classes: 'colpx-50 project-quote-author',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;

          return this.getUserDisplayValue(this.getQuoteSummary(resource.resourceId)?.assignedToUserId ?? '');
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.pricingMetric(),
        fieldName: 'resourceId',
        classes: 'colpx-50 project-quote-pricing',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;

          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return html``;

          const val = html`(${summary.percentMarginOrMarkup}%)`;

          return summary.calculation === QuotePriceCalculation.Margin
            ? html` <webmodule-tag size="small" pill variant="primary">Margin ${val}</webmodule-tag>`
            : html` <webmodule-tag size="small" pill variant="neutral">Markup ${val}</webmodule-tag>`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'lastModifiedDate',
        classes: 'colpx-50 colpxmax-180 project-quote-last-modified-date no-pseudo',
        sortable: false,
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary?.lastModifiedDate) return '';

          const dt = new Date(summary.lastModifiedDate);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-160 project-quote-state no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getQuoteSummary(resource.resourceId);

          if (!summary) return '';

          return getQuoteStatus(summary.state, true);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.netAmount(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-120 alignment-right project-quote-price no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          return this.getQuotePrice(resource);
        }
      },
      {
        title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
        fieldName: 'resourceId',
        sortable: false,
        classes: 'colpx-30 colpxmax-50 item-menu',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          return this.ellipsisMenu(resource);
        }
      }
    ];
  }

  ellipsisMenu(row: Resource) {
    const summary = this.getQuoteSummary(row.resourceId);

    return html` <webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, row)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        ${when(
          summary,
          () =>
            html` <webmodule-menu-item
              class="action-copy"
              value="copy"
              ?disabled=${containsFlag(summary!.state, QuoteState.Copying)}
            >
              <webmodule-icon slot="prefix" library="fa" name="fas-copy"></webmodule-icon>
              ${LangFranchiseeProjectResourceView.copyQuote()}
            </webmodule-menu-item>`
        )}
      </webmodule-menu>
    </webmodule-dropdown>`;
  }

  async onMenuItemSelected(event: WebmoduleSelectEvent, resource: Resource) {
    if (event.detail.item.value === 'copy') {
      await this.copyResourceEvent(resource.resourceId);
    }
  }

  public getQuoteSummary(id: string) {
    const quoteSummary = this.quoteCache.getLocalData(id)?.quoteSummary;
    return quoteSummary ? quoteSummary : null;
  }

  /**
   * Clears the resources held by this table from the cache.
   */
  public async clearResources(): Promise<void> {
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.Quote);
    if (items) {
      const quoteKeys = items.map(x => x.resourceId ?? emptyGuid);
      await this.quoteCache.updateLocal(...quoteKeys);
    }
  }

  protected quoteLink(row: Resource, value: string, copying?: boolean) {
    return copying
      ? html`<span class="quote-link disabled-quote">${value}</span>`
      : html`<a class="quote-link" href="${resolveURL(resourceQuote, row.resourceId)}" data-quoteid="${row.resourceId}"
          >${value}</a
        >`;
  }

  protected getUserDisplayValue(value: string) {
    if (value === emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }

  private getQuotePrice(row: Resource) {
    const priceSummary = this.getQuoteSummary(row.resourceId);
    return priceSummary ? moneyToTemplateResult(priceSummary.calculatedNetTotal, priceSummary.quoteCurrency) : '';
  }
}

@customElement('wm-franchiseeprojectresourcepurchaseordertable')
export class FranchiseeProjectResourcePurchaseOrderTable extends LitTableWrapper<Resource> {
  projectManager: ProjectContainerManager;
  private quoteCache = cache().quote;
  private purchaseOrderCache = cache().purchaseOrder;
  private userCache = cache().userProfile;

  private linkCache = cache().projectResourceLink;

  constructor(options: FranchiseeProjectResourceTableOptions) {
    super();
    this.projectManager = options.projectManager;
  }

  public async getRowsFromServer(_request: RequestPage): Promise<ResultPaginated<Resource>> {
    await this.projectManager.needsProject();
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.PurchaseOrder);
    if (!items)
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };

    const poKeys = items.map(x => x.resourceId ?? emptyGuid) ?? [];

    await this.purchaseOrderCache
      .preFetch(poKeys)
      .then(async () => {
        return await this.linkCache.getMany(poKeys);
      })
      .then(async (data: ItemReference<ProjectResourceLink>[] | null) => {
        const quoteIds = data?.map(x => x.data.quoteId) ?? [];
        return this.quoteCache.preFetch(quoteIds);
      });

    const authorKeys =
      items
        .map(x => {
          const summary = this.getPurchaseOrder(x.resourceId);
          return summary ? summary.assignedToUserId : null;
        })
        .filter(key => key !== null) ?? [];

    await this.userCache.preFetch(authorKeys);

    return {
      count: items.length,
      pageCount: 1,
      pageIndex: 0,
      pageSize: items.length,
      results: items
    };
  }

  public getColumns(): WebModuleLitTableColumnDef[] {
    return [
      {
        title: () => LangFranchiseeProjectResourceView.purchaseOrderNo(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-300 project-order-number',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          return this.getPurchaseOrderLink(
            resource,
            getPurchaseOrderNumberFormatted(this.getPurchaseOrder(resource.resourceId))
          );
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.quoteNo(),
        fieldName: 'resourceId',
        classes: 'colpx-50 project-order-quote-number',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          return this.quoteLink(
            this.getQuote(resource.resourceId)?.id ?? '',
            getQuoteNumberFormatted(this.getQuote(resource.resourceId))
          );
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.author(),
        fieldName: 'resourceId',
        classes: 'colpx-50 project-order-author',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;

          return this.getUserDisplayValue(this.getPurchaseOrder(resource.resourceId)?.assignedToUserId ?? '');
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.lastModified(),
        fieldName: 'lastModifiedDate',
        classes: 'colpx-50 colpxmax-180 project-order-last-modified-date no-pseudo',
        sortable: false,
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const po = this.getPurchaseOrder(resource.resourceId);

          if (!po) return '';

          const dt = new Date(po.lastModifiedDate ?? po.dateCreated);
          return `${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.status(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-160 project-order-state no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          const summary = this.getPurchaseOrder(resource.resourceId);

          if (!summary) return '';

          return getPurchaseOrderStatus(summary.state, true);
        }
      },
      {
        title: () => LangFranchiseeProjectResourceView.netCost(),
        fieldName: 'resourceId',
        classes: 'colpx-50 colpxmax-170 alignment-right project-order-price no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const resource = item as Resource;
          return this.getPrice(resource);
        }
      }
    ];
  }

  public getPurchaseOrder(id: string) {
    const po = this.purchaseOrderCache.getLocal(id)?.data.purchaseOrder;
    return po ? (po as any as PurchaseOrder) : null;
  }

  public getQuote(poId: string) {
    const qid = this.linkCache.getLocal(poId);
    const quoteSummary = this.quoteCache.getLocal(qid?.data.quoteId)?.data.quoteSummary;
    return quoteSummary ? quoteSummary : null;
  }

  /**
   * Clears the resources held by this table from the cache.
   */
  public async clearResources(): Promise<void> {
    const items = this.projectManager.container.resources?.filter(x => x.typeOf === ResourceType.PurchaseOrder);
    if (items) {
      const purchaseOrderKeys = items.map(x => x.resourceId ?? emptyGuid);
      await this.purchaseOrderCache.updateLocal(...purchaseOrderKeys);
    }
  }

  protected quoteLink(id: string, value: string) {
    return html`<a class="quote-link" href="${resolveURL(resourceQuote, id)}" data-quoteid="${id}">${value}</a>`;
  }

  private getPrice(row: Resource) {
    const po = this.getPurchaseOrder(row.resourceId);

    return po
      ? moneyToTemplateResult(po.calculatedNetTotal + po.calculatedAdjustmentTotal, po.purchaseOrderCurrency)
      : '0';
  }

  private getPurchaseOrderLink(row: Resource, value: string) {
    return html`<a
      class="purchase-order-link"
      href="${resolveURL(resourcePurchaseOrder, row.resourceId)}"
      data-purchase-order-id="${row.resourceId}"
      >${value}</a
    >`;
  }

  protected getUserDisplayValue(value: string) {
    if (value === emptyGuid) return '';
    //prefetch should already have been called
    return html`${this.userCache.getLocal(value)?.displayValue}` ?? '';
  }
}

@customElement('wm-franchiseeprojectresourceview')
export class FranchiseeProjectResourceView extends ProjectResourceView {
  private quoteTable: FranchiseeProjectResourceQuoteTable;
  private poTable: FranchiseeProjectResourcePurchaseOrderTable;
  private projectCache = cache().project;
  private projectApi = getApiFactory().project();

  constructor(options: ProjectResourceViewOptions) {
    super(options);

    this.quoteTable = this.getResourceQuoteTableFactory();
    this.poTable = this.getResourcePurchaseOrderTableFactory();
  }

  public override async invalidate(): Promise<void> {
    await super.invalidate();
    this.quoteTable.refreshData();
    this.poTable.refreshData();
  }

  public async refreshData(): Promise<void> {
    await super.refreshData();

    await this.updateResources();
    await this.quoteTable.refreshData();
    await this.poTable.refreshData();
  }

  public buttonMenu(): Snippet {
    return html`${refreshButton(async () => {
      await this.refreshData();
    })}`;
  }

  public override async updateResources(): Promise<void> {
    await this.projectManager.reloadResourcesAndDocuments();
    await this.quoteTable.clearResources();
    await this.poTable.clearResources();
  }

  protected getResourceQuoteTableFactory() {
    return new FranchiseeProjectResourceQuoteTable({
      projectManager: this.projectManager,
      copyResourceEvent: async (id: string) => {
        return await this.copyQuote(id);
      }
    });
  }

  protected getResourcePurchaseOrderTableFactory() {
    return new FranchiseeProjectResourcePurchaseOrderTable({
      projectManager: this.projectManager
    });
  }

  protected template(): EventTemplate {
    return html`
      <h2 class="section-header">${LangFranchiseeProjectResourceView.quotesSectionHeader()}</h2>
      <div class="row">${this.quoteTable}</div>

      <h2 class="section-header">${LangFranchiseeProjectResourceView.purchaseOrdersSectionHeader()}</h2>
      <div class="row">${this.poTable}</div>
    `;
  }

  private async copyQuote(id: string): Promise<boolean> {
    //REFACTOR -> put this logic into the container manager
    const qcm = getQuoteContainerManager(getQuoteContainer(id));
    await qcm.needsQuote();
    const projectState = this.projectManager.project.state;
    const engine = new QuoteSetSiblingManager(() => qcm.siblings ?? [], qcm.quote);

    const isProjectActive = projectState !== ProjectState.Cancelled && projectState !== ProjectState.Completed;
    if (isProjectActive && !engine.isNewQuoteAllowed) {
      if (
        !(await AskConfirmation(
          LangFranchiseeProjectResourceView.cannotAcceptNewCopies({
            quoteNumber: qcm.quote.quoteNumber,
            quoteNumberFormatted: getQuoteNumberFormatted(qcm.quote)
          }),
          getConfirmationButtons()[ConfirmationButtonType.continueCancel]
        ))
      )
        return false;
    }

    const makeNewQuoteFromOld = async () => {
      qcm.generateQuoteNumberOnSave();
      return qcm.makeCopy();
    };
    const copy = isProjectActive
      ? engine.isNewQuoteAllowed
        ? await qcm.makeAlternative()
        : await makeNewQuoteFromOld()
      : await qcm.makeCopy();
    if (copy) {
      if (isProjectActive) {
        //TODO# when  we implement supplier reviews we will need to be able to execute this quote as an
        //after save event of a quote.
        const resource = await this.projectApi.createProjectResourceReference({
          resourceId: copy.quoteId,
          projectId: this.projectManager.projectId,
          typeOf: ResourceType.Quote,
          parentResourceId: emptyGuid,
          parentTypeOf: ResourceType.None
        });
        if (resource) {
          this.projectCache.updateLocal(this.projectManager.projectId);

          await this.refreshData();
        } else {
          console.log(`quote copy failed to generate project resource. deleting quote`);
          // if we were unable to create the project link, remove the alternative quote
          return await getQuoteContainerManager(copy).deleteQuote();
        }
      }
      if (containsFlag(copy.quote!.state, QuoteState.Copying)) {
        await monitorQuoteCopyCompletion(copy.quoteId);
        return true;
      } else launchQuote(copy.quoteId);
    }
    fireQuickErrorToast(LangFranchiseeProjectResourceView.unableToCopyQuote());
    return false;
  }
}
